/** Placeholder cutoffs — science owns the real ones, hence the parameter. */
export const DEFAULT_GUT_X_AXIS_THRESHOLDS = {
    moderate: 1,
    high: 3,
};
/**
 * Whether a condition itself came back flagged. Its own grade, not its metrics —
 * the count on a condition's card is `flagged_count`, which the query supplies.
 */
const isFlagged = (evaluation) => evaluation === 'High' || evaluation === 'Medium';
const EVALUATION_ORDER = {
    High: 0,
    Medium: 1,
    Low: 2,
};
/**
 * Buckets a flagged-condition count into the organ's colour.
 * @param {number} flaggedCount - High/Medium conditions on the organ.
 * @param {GutXAxisFlagThresholds} thresholds - Inclusive lower bounds.
 * @returns {GutXAxisFlagLevel} The bucket for that count.
 */
export function resolveGutXAxisFlagLevel(flaggedCount, thresholds = DEFAULT_GUT_X_AXIS_THRESHOLDS) {
    if (flaggedCount >= thresholds.high)
        return 'high';
    if (flaggedCount >= thresholds.moderate)
        return 'moderate';
    return 'low';
}
/**
 * Groups organ/condition join rows into organs and resolves each one's colour.
 * Organs sort by `priority` ascending, conditions High -> Medium -> Low then
 * alphabetically.
 * @param {GutXAxisOrganConditionRow[]} rows - One row per organ/condition pair.
 * @param {GutXAxisFlagThresholds} thresholds - Inclusive lower bounds.
 * @returns {GutXAxisOrgan[]} Grouped organs for the body component.
 */
export function buildGutXAxisOrgans(rows, thresholds = DEFAULT_GUT_X_AXIS_THRESHOLDS) {
    const organsMap = new Map();
    const seenConditions = new Map();
    for (const row of rows) {
        let organ = organsMap.get(row.organ_id);
        if (!organ) {
            organ = {
                id: row.organ_id,
                lookup_code: row.organ_lookup_code,
                display_name: row.organ_display_name,
                priority: row.organ_priority,
                about: row.organ_about,
                conditions: [],
                flagged_count: 0,
                flag_level: 'low',
            };
            organsMap.set(row.organ_id, organ);
            seenConditions.set(row.organ_id, new Set());
        }
        // A widened join could repeat a condition and shift the organ's colour.
        const seen = seenConditions.get(row.organ_id);
        if (seen.has(row.condition_id))
            continue;
        seen.add(row.condition_id);
        organ.conditions.push({
            id: row.condition_id,
            condition: row.condition,
            display_name: row.condition_display_name,
            evaluation: row.evaluation,
            flagged_count: row.condition_flagged_count,
            total_count: row.condition_total_count,
            marked_in_survey: row.marked_in_survey,
            sensitive: row.sensitive,
        });
        if (isFlagged(row.evaluation))
            organ.flagged_count += 1;
    }
    const organs = Array.from(organsMap.values());
    for (const organ of organs) {
        organ.conditions.sort((a, b) => EVALUATION_ORDER[a.evaluation] - EVALUATION_ORDER[b.evaluation] ||
            a.display_name.localeCompare(b.display_name));
        organ.flag_level = resolveGutXAxisFlagLevel(organ.flagged_count, thresholds);
    }
    return organs.sort((a, b) => a.priority - b.priority);
}
//# sourceMappingURL=buildGutXAxisOrgans.js.map